import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/hooks/useLanguage';
import { fetchRothusMagazines, type MagazineIssue } from '@/lib/rothusMagazines';
import { currentIssueCopy, selectCurrentRothusIssue } from '@/lib/currentRothusIssue';
import MagazineReaderDialog from '@/components/MagazineReaderDialog';

const CurrentIssueSection = () => {
  const { language } = useLanguage();
  const sectionRef = useRef<HTMLElement>(null);
  const [offset, setOffset] = useState(0);
  const [issue, setIssue] = useState<MagazineIssue | null>(null);
  const [readerOpen, setReaderOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      const issues = await fetchRothusMagazines({ fallback: 'last-success' });
      if (active) setIssue(selectCurrentRothusIssue(issues));
    };
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    void refresh();
    const interval = window.setInterval(refresh, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // Subtle parallax on the magazine cover
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = sectionRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        // progress from -1 (below) to 1 (above)
        const progress = (vh / 2 - (rect.top + rect.height / 2)) / vh;
        setOffset(Math.max(-1, Math.min(1, progress)) * 18);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  if (!issue) return null;
  const copy = currentIssueCopy(issue, language);
  const stories = copy.highlights;
  const monthYear = new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'de-CH', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(issue.publication_date));


  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-paper py-24 md:py-32 lg:py-40"
      aria-label={language === 'en' ? 'Current Issue' : 'Aktuelle Ausgabe'}
    >
      {/* Winter-inspired background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-snow/40 via-paper to-paper" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              'radial-gradient(1200px 500px at 10% 10%, hsl(var(--ice) / 0.10), transparent 60%), radial-gradient(900px 400px at 90% 80%, hsl(var(--ice) / 0.08), transparent 60%)',
          }}
        />
        {/* Fine grain */}
        <div
          className="absolute inset-0 opacity-[0.05] mix-blend-multiply"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      <div className="relative container mx-auto px-5 sm:px-8">
        <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Magazine mockup */}
          <div className="lg:col-span-6 order-1 lg:order-2 flex justify-center lg:justify-end">
            <div
              className="relative"
              style={{
                perspective: '1600px',
                transform: `translateY(${offset}px)`,
                transition: 'transform 120ms linear',
              }}
            >
              {/* Soft floor shadow */}
              <div
                aria-hidden
                className="absolute left-1/2 -translate-x-1/2 bottom-[-38px] h-10 w-[75%] rounded-[50%] bg-ink/25 blur-2xl opacity-40"
              />

              {/* Magazine */}
              <div
                className="relative animate-[floaty_8s_ease-in-out_infinite]"
                style={{
                  transform: 'rotateY(-18deg) rotateX(4deg)',
                  transformStyle: 'preserve-3d',
                }}
              >
                {/* Back pages stack */}
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-[6px] bg-white/70 shadow-[0_10px_40px_-15px_rgba(18,20,22,0.35)]"
                  style={{ transform: 'translateZ(-12px) translateX(6px)' }}
                />
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-[6px] bg-white/85 shadow-[0_15px_50px_-20px_rgba(18,20,22,0.35)]"
                  style={{ transform: 'translateZ(-6px) translateX(3px)' }}
                />

                {/* Cover */}
                <div
                  className="relative rounded-[6px] overflow-hidden bg-white shadow-[0_40px_80px_-30px_rgba(18,20,22,0.55),0_20px_40px_-20px_rgba(18,20,22,0.35)] w-[260px] h-[360px] sm:w-[300px] sm:h-[416px] md:w-[340px] md:h-[471px] lg:w-[380px] lg:h-[527px]"
                >
                  {issue.cover_image_url && <img
                    src={issue.cover_image_url}
                    alt={`glide ${issue.issue_number} — ${copy.title}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />}

                  {/* Spine highlight */}
                  <div
                    aria-hidden
                    className="absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-black/30 via-black/10 to-transparent"
                  />
                  {/* Cover sheen */}
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-white/20 mix-blend-overlay"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Editorial content */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 rounded-full border border-ice/40 bg-white/70 backdrop-blur px-3 py-1 text-[11px] font-medium tracking-[0.18em] uppercase text-ice">
              <Sparkles className="h-3 w-3" strokeWidth={2} />
              {language === 'en' ? 'New Issue' : 'Neue Ausgabe'}
            </div>

            <p className="mt-6 text-xs tracking-[0.28em] uppercase text-ink/50">
              {language === 'en' ? 'Issue' : 'Ausgabe'} {issue.issue_number} · {monthYear}
            </p>

            <h2 className="mt-3 font-display text-[2.5rem] sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              {copy.title}
            </h2>

            <p className="mt-6 max-w-lg text-base sm:text-lg leading-relaxed text-ink/70">
              {copy.teaser}
            </p>


            {stories.length > 0 && <ul className="mt-8 space-y-3 max-w-lg">
              {stories.map((s, i) => (
                <li key={i} className="flex items-start gap-4 group">
                  <span className="mt-2 h-px w-8 bg-ice/60 group-hover:w-12 transition-all duration-300" />
                  <span className="text-ink/85 text-[15px] leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>}

            <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button
                  onClick={() => setReaderOpen(true)}
                  size="lg"
                  className="group bg-ink text-paper hover:bg-ink/90 rounded-full px-7 h-12 text-sm tracking-wide"
                >
                  {language === 'en' ? 'Discover current issue' : 'Aktuelle Ausgabe entdecken'}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              <Link
                to="/shop#abonnements"
                className="group inline-flex items-center justify-center rounded-full border border-ink/20 bg-transparent px-7 h-12 text-sm tracking-wide text-ink hover:bg-ink hover:text-paper transition-colors"
              >
                {language === 'en' ? 'Subscribe to glide' : 'glide abonnieren'}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <MagazineReaderDialog issue={readerOpen ? issue : null} onClose={() => setReaderOpen(false)} />

      <style>{`
        @keyframes floaty {
          0%, 100% { transform: rotateY(-18deg) rotateX(4deg) translateY(0px); }
          50%      { transform: rotateY(-18deg) rotateX(4deg) translateY(-8px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-\\[floaty_8s_ease-in-out_infinite\\] { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

export default CurrentIssueSection;
